"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { LIKERT_SCALE_ANALYZER_STRINGS } from "@/components/tools/likert-scale-analyzer";
import { mean, standardDeviation } from "@/lib/stats/descriptive";
import { downloadText } from "@/lib/tool-kit/download";
import {
  DASH,
  type NumberFormatter,
} from "@/lib/tool-kit/research-design/format";
import {
  type CompositeMethod,
  compositeScores,
  type LikertScale,
} from "@/lib/tool-kit/research-design/likert";
import { toCsv } from "@/lib/tool-kit/research-design/matrix";

/** Composite (scale) scores per respondent, with reverse-coding chosen item by item. */
export function LikertComposite({
  values,
  itemNames,
  respondentNames,
  scale,
  strings,
  fmt,
}: {
  values: readonly (readonly (number | null)[])[];
  itemNames: readonly string[];
  respondentNames: readonly string[];
  scale: LikertScale;
  strings: typeof LIKERT_SCALE_ANALYZER_STRINGS;
  fmt: NumberFormatter;
}) {
  const [method, setMethod] = useState<CompositeMethod>("sum");
  const [reversed, setReversed] = useState<number[]>([]);
  const scores = compositeScores(
    values,
    itemNames.length,
    new Set(reversed),
    scale,
    method
  );
  const valid = scores.flatMap((row) =>
    row.score === null ? [] : [row.score]
  );
  const show = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const stat = (enough: boolean, value: () => number) =>
    enough ? show(value()) : DASH;
  const head = [strings.colRespondent, strings.colScore, strings.colAnswered];

  function toggle(item: number, on: boolean) {
    setReversed((current) =>
      on ? [...current, item] : current.filter((value) => value !== item)
    );
  }

  function download() {
    const rows = scores.map((row, i) => [
      respondentNames[i],
      row.score,
      row.answered,
    ]);
    downloadText(
      strings.scoresFile,
      toCsv([head, ...rows]),
      "text/csv;charset=utf-8"
    );
  }

  return (
    <div className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.compositeHeading}</h3>
      <PillGroup
        label={strings.methodLegend}
        name="lk-method"
        onChange={setMethod}
        options={[
          { value: "sum", label: strings.methodSum },
          { value: "mean", label: strings.methodMean },
        ]}
        value={method}
      />
      <fieldset className="space-y-2">
        <legend className={LABEL}>{strings.reverseLegend}</legend>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {itemNames.map((name, item) => (
            <Checkbox
              checked={reversed.includes(item)}
              key={name}
              label={name}
              onChange={(on) => toggle(item, on)}
            />
          ))}
        </div>
        <p className={HINT}>{strings.reverseHint}</p>
      </fieldset>
      <StatGrid>
        <StatCard label={strings.scored} value={fmt(valid.length)} />
        <StatCard
          emphasis
          label={strings.compositeMean}
          value={stat(valid.length > 0, () => mean(valid))}
        />
        <StatCard
          label={strings.compositeSd}
          value={stat(valid.length > 1, () => standardDeviation(valid))}
        />
        <StatCard
          label={strings.compositeMin}
          value={stat(valid.length > 0, () => Math.min(...valid))}
        />
        <StatCard
          label={strings.compositeMax}
          value={stat(valid.length > 0, () => Math.max(...valid))}
        />
      </StatGrid>
      <p className={HINT}>{strings.compositeRule}</p>
      <details className="space-y-2">
        <summary className={`${LABEL} cursor-pointer`}>
          {strings.showScores}
        </summary>
        <DataTable
          head={head}
          rows={scores.map((row, i) => [
            respondentNames[i],
            row.score === null ? DASH : show(row.score),
            fmt(row.answered),
          ])}
        />
      </details>
      <button className={BUTTON_SECONDARY} onClick={download} type="button">
        {strings.downloadScores}
      </button>
    </div>
  );
}
